import { computed } from '@angular/core';
import type { FieldTree } from '@angular/forms/signals';
import { validateStandardSchema } from '@angular/forms/signals';

import type {
    EduLoginButtonEntity,
    EduLoginButtonItem,
    LegacyEduLoginButtonDefault,
    LegacyEduLoginButtonZero,
} from '@sites/entity-core';
import { eduLoginButtonLegacyDefaultV0Schema, eduLoginButtonLegacyZeroV0Schema } from '@sites/entity-core';

import type { BaseLegacyEntityForm, BaseLegacyEntityFormOptions } from '../../core/create-base-legacy-entity-form';
import { configureCommonReadonly, createBaseLegacyEntityForm } from '../../core/create-base-legacy-entity-form';
import { arrayMove } from '../../core/array-move';
import { composeForm } from '../../core/compose-form';
import { animationPlugin } from '../../plugins/widget/animation.plugin';
import { extendedPositionPlugin, widgetPositionPlugin } from '../../plugins/widget/position.plugin';
import { visibilityPlugin } from '../../plugins/widget/visibility.plugin';
import { legacyWidgetEnvelopePlugin } from '../../plugins/widget/legacy-widget-envelope.plugin';

export type EduLoginButtonFormModel = EduLoginButtonEntity;
export type EduLoginButtonDataModel = EduLoginButtonEntity['data'];
export type { EduLoginButtonItem };

/**
 * EDU-login button accessors — a structural fork of the button widget's
 * `buttonSharedAccessors`. Shared verbatim by both variant assemblers (only
 * `position` differs, and that comes from the position plugins).
 */
function eduLoginButtonSharedAccessors(
    raw: () => FieldTree<LegacyEduLoginButtonDefault> | FieldTree<LegacyEduLoginButtonZero>
) {
    const r = raw as () => FieldTree<LegacyEduLoginButtonDefault>;
    return {
        get buttons() {
            return r().data.buttons;
        },
        buttonItems: computed<EduLoginButtonItem[]>(() => r().data.buttons?.().value() ?? []),
        setButtons: (value: EduLoginButtonItem[]) => {
            r().data.buttons().value.set(value);
        },
        buttonAt: (index: number): FieldTree<EduLoginButtonItem> | undefined => {
            return (r().data.buttons as unknown as Record<number, FieldTree<EduLoginButtonItem>>)[index];
        },
        addButton: (item: EduLoginButtonItem) => {
            r()
                .data.buttons()
                .value.update((arr) => [...arr, item]);
        },
        removeButton: (index: number) => {
            r()
                .data.buttons()
                .value.update((arr) => arr.filter((_, i) => i !== index));
        },
        moveButton: (from: number, to: number) => {
            r()
                .data.buttons()
                .value.update((arr) => arrayMove(arr, from, to));
        },
        updateButton: (index: number, patch: Partial<EduLoginButtonItem>) => {
            r()
                .data.buttons()
                .value.update((arr) => arr.map((b, i) => (i === index ? { ...b, ...patch } : b)));
        },

        // Group layout
        get horizontalIndent() {
            return r().data.horizontalIndent;
        },
        setHorizontalIndent: (value: EduLoginButtonDataModel['horizontalIndent']) => {
            r().data.horizontalIndent().value.set(value);
        },
        get horizontalAlign() {
            return r().data.horizontalAlign;
        },
        setHorizontalAlign: (value: EduLoginButtonDataModel['horizontalAlign']) => {
            r().data.horizontalAlign().value.set(value);
        },
        get mobileHorizontalAlign() {
            return r().data.mobileHorizontalAlign;
        },
        setMobileHorizontalAlign: (value: EduLoginButtonDataModel['mobileHorizontalAlign']) => {
            r().data.mobileHorizontalAlign().value.set(value);
        },
        get tabletHorizontalAlign() {
            return r().data.tabletHorizontalAlign;
        },
        setTabletHorizontalAlign: (value: EduLoginButtonDataModel['tabletHorizontalAlign']) => {
            r().data.tabletHorizontalAlign().value.set(value);
        },
        get isSynchronizeStyle() {
            return r().data.isSynchronizeStyle;
        },
        isSynchronized: computed<boolean>(() => Boolean(r().data.isSynchronizeStyle?.().value())),
        setIsSynchronizeStyle: (value: boolean) => {
            r().data.isSynchronizeStyle().value.set(value);
        },
    };
}

function assembleDefaultEduLoginButtonForm(base: BaseLegacyEntityForm<LegacyEduLoginButtonDefault>) {
    const _raw = base._raw;

    const widenedBase: BaseLegacyEntityForm<EduLoginButtonEntity> = base;

    return composeForm(
        widenedBase,
        legacyWidgetEnvelopePlugin(() => _raw),
        eduLoginButtonSharedAccessors(() => _raw),
        visibilityPlugin(() => _raw.data),
        animationPlugin(() => _raw.data),
        // Default-only layout — the simple per-device position grid.
        widgetPositionPlugin(() => _raw.data)
    );
}

function assembleZeroEduLoginButtonForm(base: BaseLegacyEntityForm<LegacyEduLoginButtonZero>) {
    const _raw = base._raw;

    const widenedBase: BaseLegacyEntityForm<EduLoginButtonEntity> = base;

    return composeForm(
        widenedBase,
        legacyWidgetEnvelopePlugin(() => _raw),
        eduLoginButtonSharedAccessors(() => _raw),
        visibilityPlugin(() => _raw.data),
        animationPlugin(() => _raw.data),
        // Zero-only layout — the full extended-position grid.
        extendedPositionPlugin(() => _raw.data)
    );
}

/**
 * Signal-form view of a **default-placement** EDU-login button widget. Validates
 * against the legacy default envelope schema (name literal `'edu-login-button'`),
 * which permits an absent `type` field for parity with pre-discriminator data.
 */
export function createLegacyEduLoginButtonDefaultForm(
    options?: BaseLegacyEntityFormOptions<LegacyEduLoginButtonDefault>
) {
    const base = createBaseLegacyEntityForm<LegacyEduLoginButtonDefault>((schemaPath) => {
        validateStandardSchema(schemaPath, eduLoginButtonLegacyDefaultV0Schema);
        configureCommonReadonly(schemaPath);
    }, options);

    return assembleDefaultEduLoginButtonForm(base);
}

/**
 * Signal-form view of a **zero-block-placement** EDU-login button widget.
 * Validates against the legacy zero envelope schema, which requires
 * `type: 'zero'` on the envelope.
 */
export function createLegacyEduLoginButtonZeroForm(options?: BaseLegacyEntityFormOptions<LegacyEduLoginButtonZero>) {
    const base = createBaseLegacyEntityForm<LegacyEduLoginButtonZero>((schemaPath) => {
        validateStandardSchema(schemaPath, eduLoginButtonLegacyZeroV0Schema);
        configureCommonReadonly(schemaPath);
    }, options);

    return assembleZeroEduLoginButtonForm(base);
}

/**
 * Variant dispatcher. Callers that only know `EduLoginButtonEntity` (the union
 * of default + zero) use this — `WebLegacyWidgetSignalFormService` for instance — and
 * the dispatcher reads the variant tag from `options.initialData?.type` to pick
 * the matching per-variant factory.
 */
export function createEduLoginButtonForm(options?: BaseLegacyEntityFormOptions<EduLoginButtonEntity>) {
    const variant = options?.initialData?.type;

    if (variant === 'zero') {
        return createLegacyEduLoginButtonZeroForm(options as BaseLegacyEntityFormOptions<LegacyEduLoginButtonZero>);
    }

    return createLegacyEduLoginButtonDefaultForm(options as BaseLegacyEntityFormOptions<LegacyEduLoginButtonDefault>);
}

export type EduLoginButtonForm = ReturnType<typeof createEduLoginButtonForm>;
export type EduLoginButtonDefaultForm = ReturnType<typeof createLegacyEduLoginButtonDefaultForm>;
export type EduLoginButtonZeroForm = ReturnType<typeof createLegacyEduLoginButtonZeroForm>;
